/**
 * @fileoverview
 * This file defines the `commonRoutesConfig` object, which provides route configuration
 * for a React application using lazy-loaded components and access control logic.
 * Each route includes metadata such as the path, associated React component,
 * and access control settings based on user subscription levels and roles.
 */

import { SCol, SRow } from "@avalara/skylab-sdk/react";
import React from "react";
import featureFlag from "../../featureToggler/featureFlag";
import { CoverLetterProps } from "../coverLetters/types";
import { CustomerProps } from "../customers/types";
import ECMS from "../ecms/ECMS";
import BucketLocationGrid from "../settings/Buckets/manageLocations/BucketLocationGrid";
import BucketUserGrid from "../settings/Buckets/manageUsers/BucketUserGrid";
import OutgoingCertificateContext from "../settings/customFields/certificatesCustomFields/OutgoingCertificateContext";
import { Permissions } from "./Permissions";
import {
    advanceSubscription,
    EcmRole,
    ecmSubscription,
    lowerSubscription,
    Subscription,
    userRoleAdminSubscription,
    vendorAvaTaxAPSubscription,
    vendorPremiumOnlySubscription,
    vendorSubscription,
} from "./Subscription";
import type { RouteConfig } from "./types";
import { addFullPathToConfig } from "./utils";

// All React components are dynamically imported for code-splitting benefits using React.lazy.
// This enhances performance by loading components only when they are needed.
const ImportCustomers = React.lazy(() => import("../bulkImport/ImportCustomers"));
const ImportCertificates = React.lazy(() => import("../bulkImport/ImportCertificates"));
const ImportVendors = React.lazy(() => import("../bulkImport/ImportVendors"));
const Campaign = React.lazy(() => import("../campaigns/Campaign"));

const CertificateForms = React.lazy(() => import("../content/CertificateForms/CertificateForms"));
const ExemptEntities = React.lazy(() => import("../content/ExemptEntities/ExemptEntities"));
const TaxAnswers = React.lazy(() => import("../content/TaxAnswers/TaxAnswers"));
const TaxLaws = React.lazy(() => import("../content/TaxLaws/TaxLaws"));
const CoverLetter = React.lazy(() => import("../coverLetters/CoverLetter"));
const ManagedServices = React.lazy(() => import("../managedServices/ManagedServices"));
const MsWorklist = React.lazy(() => import("../managedServices/Worklist/MsWorklist"));
const MsCertificates = React.lazy(() => import("../managedServices/Certificates/MsCertificates"));
const MsAccounts = React.lazy(() => import("../managedServices/Accounts/MsAccounts"));
const MsEnabledForms = React.lazy(() => import("../managedServices/Forms/MsEnabledForms"));
const MsRules = React.lazy(() => import("../managedServices/Rules/MsRules"));
const CoverLetterGrid = React.lazy(() => import("../coverLetters/CoverLetterGrid"));
const CoverLetterHeader = React.lazy(() => import("../coverLetters/CoverLetterHeader"));
const VendorLetterGrid = React.lazy(() => import("../coverLetters/VendorLetterGrid"));
const Customer = React.lazy(() => import("../customers/Customer"));
const CustomerHeader = React.lazy(() => import("../customers/CustomerHeader"));
const HealthCheck = React.lazy(() => import("../healthCheck/HealthCheck"));
const JobForm = React.lazy(() => import("../jobs/JobForm"));
const JobDetails = React.lazy(() => import("../jobs/JobDetails"));
const JobsSearch = React.lazy(() => import("../search/jobs/JobsSearch"));
const Overview = React.lazy(() => import("../overview/Overview"));
const Resources = React.lazy(() => import("../overview/resources/Resources"));
const Promo = React.lazy(() => import("../provision/Promo"));
const RequestDashboard = React.lazy(() => import("../requests/RequestDashboard"));
const Requests = React.lazy(() => import("../requests/Requests"));
const BulkRequest = React.lazy(() => import("../requests/bulkRequest/BulkRequest"));
const SingleRequest = React.lazy(() => import("../requests/singleRequest/SingleRequest"));
const VendorRequest = React.lazy(() => import("../requests/vendorRequests/VendorRequest"));
const ValidationHistory = React.lazy(() => import("../review/ValidationHistory"));
const Certificate = React.lazy(() => import("../review/certificate/Certificate"));
const CertificateDetail = React.lazy(() => import("../review/certificate/CertificateDetail"));
const Validation = React.lazy(() => import("../review/validation/Validation"));
const AviatorValidation = React.lazy(() => import("../aviator/review/AviatorValidation"));
const AviatorCertificate = React.lazy(() => import("../aviator/certificate/AviatorCertificate"));
const RoleManagementPage = React.lazy(() => import("../roleManagementPage/RoleManagementPage"));

const BulkUploadCertificates = React.lazy(
    () => import("../search/BulkUpload/BulkUploadCertificates")
);
const SavedSearches = React.lazy(() => import("../search/SavedSearches/SavedSearches"));
const VendorCertificates = React.lazy(
    () => import("../search/VendorCertificates/VendorCertificate")
);
const VendorCertificateHeader = React.lazy(
    () => import("../search/VendorCertificates/VendorCertificateHeader")
);
const SendPackage = React.lazy(() => import("../search/VendorPackages/SendPackage"));
const VendorPackageContent = React.lazy(
    () => import("../search/VendorPackages/VendorPackageContent")
);
const VendorPackageCreate = React.lazy(
    () => import("../search/VendorPackages/VendorPackageCreate")
);
const VendorPackageEdit = React.lazy(() => import("../search/VendorPackages/VendorPackageEdit"));
const Certificates = React.lazy(() => import("../search/certificates/Certificates"));
const Customers = React.lazy(() => import("../search/customers/Customers"));
const PendingSearch = React.lazy(() => import("../search/pending/PendingSearch"));
const Vendors = React.lazy(() => import("../search/vendors/Vendors"));

const ManageExemptionReasons = React.lazy(
    () => import("../settings/ManageExemptionReasons/ManageExemptionReasons")
);
const Settings = React.lazy(() => import("../settings/Setting"));
const CustomFields = React.lazy(() => import("../settings/customFields/CustomFields"));
const CertificatesCustomFields = React.lazy(
    () => import("../settings/customFields/certificatesCustomFields/CertificatesCustomFields")
);
const CustomersCustomFields = React.lazy(
    () => import("../settings/customFields/customersCustomFields/CustomersCustomFields")
);
const ManageInvalidReasons = React.lazy(
    () => import("../settings/invalidReason/ManageInvalidReasons")
);
const Labels = React.lazy(() => import("../settings/labels/Labels"));
const CertificateLabels = React.lazy(
    () => import("../settings/labels/certificatesLabels/CertificateLabels")
);
const CertificateAttachmentLabels = React.lazy(
    () => import("../settings/labels/certificateAttachmentLabels/CertificateAttachmentLabels")
);
const CustomerLabels = React.lazy(
    () => import("../settings/labels/customersLabels/CustomerLabels")
);
const Region = React.lazy(() => import("../settings/region/Region"));
const Retail = React.lazy(() => import("../settings/retail/Retail"));
const RetailLocations = React.lazy(
    () => import("../settings/retail/retailLocations/RetailLocations")
);
const RetailSettings = React.lazy(() => import("../settings/retail/retailSettings/RetailSettings"));
const AdvancedSettings = React.lazy(() => import("../settings/tabs/AdvancedSettings"));
const CertExpressSettings = React.lazy(() => import("../settings/tabs/CertExpressSettings"));
const CompanyGroupsSettings = React.lazy(
    () => import("../settings/companyGroups/CompanyGroupsSettings")
);
const BucketsSetting = React.lazy(() => import("../settings/Buckets/BucketsSetting"));
const CertificateOutgoingRequestFormsFilter = React.lazy(
    () => import("../settings/tabs/CertificateRequestForms/CertificateOutgoingRequestFormsFilter")
);
const CertificateOutgoingRequestFormsGrid = React.lazy(
    () => import("../settings/tabs/CertificateRequestForms/CertificateOutgoingRequestFormsGrid")
);
const CertificateRequestForms = React.lazy(
    () => import("../settings/tabs/CertificateRequestForms/CertificateRequestForms")
);
const CertificateRequestFormsHome = React.lazy(
    () => import("../settings/tabs/CertificateRequestForms/CertificateRequestFormsHome")
);
const CertificateRequestFormsContent = React.lazy(
    () => import("../settings/tabs/CertificateRequestForms/CertificateRequestFormsContent")
);
const CertificateRequestFormsFilter = React.lazy(
    () => import("../settings/tabs/CertificateRequestForms/CertificateRequestFormsFilter")
);
const CertificateRequestFormsGrid = React.lazy(
    () => import("../settings/tabs/CertificateRequestForms/CertificateRequestFormsGrid")
);
const CertificateRequests = React.lazy(() => import("../settings/tabs/CertificateRequests"));
const CertificateSettingsTab = React.lazy(() => import("../settings/tabs/Certificates"));
const General = React.lazy(() => import("../settings/tabs/General"));
const InternalUserSettings = React.lazy(() => import("../settings/tabs/InternalUserSettings"));
const ManageProductCategories = React.lazy(
    () => import("../settings/productCategories/ManageProductCategories")
);
const UserPreferences = React.lazy(() => import("../settings/userPreferences/UserPreferences"));

const ECMReports = React.lazy(() => import("../ECMReports/ECMReports"));
const AdminReports = React.lazy(() => import("../ECMReports/AdminReports"));
const IamEnablement = React.lazy(() => import("../settings/iamEnablement/Enablement"));
const IamEnablementDetails = React.lazy(
    () => import("../settings/iamEnablement/EnablementDetails")
);
const IamEnablementLogs = React.lazy(() => import("../settings/iamEnablement/EnablementLogs"));
const BulkEnablementJobs = React.lazy(() => import("../settings/iamEnablement/BulkEnablementJobs"));

const Home = React.lazy(() => import("../home"));

// TODO - Remove these asserted components once the original components are refactored to use .tsx extension
const CustomerComponent = Customer as React.ComponentType<CustomerProps>;
const CoverLetterComponent = CoverLetter as React.ComponentType<CoverLetterProps>;

const EXEMPTIONS_BREADCRUMB = {
    label: "Exemptions",
    url: "#",
    dataRef: "1",
    emitNavEvent: true,
};

// Shared by review, reviewCertificate, and reviewCertificateHistory below - they're
// sibling routes (not nested) so each needs its own copy to be picked up by
// findActiveRoute's breadcrumb resolution.
const REVIEW_BREADCRUMBS = [
    EXEMPTIONS_BREADCRUMB,
    {
        label: "Incoming certificates to review",
        url: "/search/pending",
        dataRef: "2",
        emitNavEvent: true,
    },
    {
        label: "Review exemption certificate",
        dataRef: "3",
    },
];

/**
 * Contains the routing configuration for various sections of the application.
 * Each key in the object represents a route identifier, mapping to a configuration
 * object that specifies the route's path, associated React component (`element`),
 * and an `accessConfig` object to control access based on user roles and subscriptions.
 */
const commonRoutesConfig: RouteConfig = Object.freeze(
    addFullPathToConfig({
        home: {
            path: "/",
            element: <Home />,
        },
        overview: {
            path: "/overview",
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
            },
            element: <Overview />,
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Overview",
                    dataRef: "2",
                },
            ],
        },
        managedServices: {
            path: "/managed-services",
            element: <ManagedServices />,
            // Gated on internal (TSA) users only; the managed_service_user role is no longer required.
            accessConfig: {
                internalUser: true,
            },
            children: {
                // Worklist is the landing tab: it takes the bare base path.
                // `/managed-services/worklist` remains a deep-link alias to the same component.
                // See `ManagedServices.tsx`'s `isSelected` regex for why `worklist` must not join
                // the tab-selection exclusion list.
                index: {
                    index: true,
                    element: <MsWorklist />,
                },
                worklist: {
                    path: "worklist",
                    element: <MsWorklist />,
                },
                certificates: {
                    path: "certificates",
                    element: <MsCertificates />,
                },
                accounts: {
                    path: "accounts",
                    element: <MsAccounts />,
                },
                forms: {
                    path: "forms",
                    element: <MsEnabledForms />,
                },
                rules: {
                    path: "rules",
                    element: <MsRules />,
                },
            },
        },
        settings: {
            path: "/settings",
            element: <Settings />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                allowedCEMRoles: [
                    EcmRole.CEMAdmin,
                    EcmRole.CEMManager,
                    EcmRole.CEMCertificateReviewer,
                    EcmRole.CEMViewer,
                    EcmRole.CEMPreValidator,
                ],
                permissions: [Permissions.settings.index.generalRead],
            },
            children: {
                index: {
                    index: true,
                    element: <General />,
                    accessConfig: {
                        allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                    },
                },
                general: {
                    path: "general",
                    element: <General />,
                    accessConfig: {
                        allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                    },
                },
                certificateSettings: {
                    path: "certificate-settings",
                    element: <CertificateSettingsTab />,
                    accessConfig: {
                        allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                    },
                },
                certificateRequests: {
                    path: "certificateRequests",
                    element: <CertificateRequests />,
                    accessConfig: {
                        allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                    },
                },
                defaultExemptionForms: {
                    path: "default-exemption-forms",
                    element: <CertificateRequestForms />,
                    accessConfig: {
                        allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                        name: "form",
                    },
                    children: {
                        index: {
                            index: true,
                            element: <CertificateRequestFormsHome />,
                        },
                        incoming: {
                            path: "incoming",
                            element: (
                                <SRow>
                                    <SCol>
                                        <CertificateRequestFormsFilter />
                                        <CertificateRequestFormsGrid />
                                    </SCol>
                                </SRow>
                            ),
                            accessConfig: {
                                allowedSubscriptions: advanceSubscription,
                            },
                        },
                        outgoing: {
                            path: "outgoing",
                            element: (
                                <SRow>
                                    <SCol>
                                        <CertificateOutgoingRequestFormsFilter />
                                        <CertificateOutgoingRequestFormsGrid />
                                    </SCol>
                                </SRow>
                            ),
                            accessConfig: {
                                allowedSubscriptions: vendorSubscription,
                            },
                        },
                    },
                },
                certExpressSettings: {
                    path: "certexpress-settings",
                    element: <CertExpressSettings />,
                    accessConfig: {
                        allowedSubscriptions: advanceSubscription,
                    },
                },
                advancedSettings: {
                    path: "advanced-settings",
                    element: <AdvancedSettings />,
                    accessConfig: {
                        allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                    },
                },
                internalUserSettings: {
                    // IAM permissions need to be implemented, need clarity.
                    path: "internal-user-settings",
                    element: <InternalUserSettings />,
                    accessConfig: {
                        allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                        internalUser: true,
                    },
                },
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    dataRef: "2",
                },
            ],
        },
        userPreferences: {
            path: "/settings/userPreferences",
            element: <UserPreferences />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                allowedCEMRoles: [
                    EcmRole.CEMAdmin,
                    EcmRole.CEMManager,
                    EcmRole.CEMCertificateReviewer,
                    EcmRole.CEMViewer,
                    EcmRole.CEMPreValidator,
                ],
                permissions: [Permissions.settings.userPreferences.update],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/advanced-settings",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "User preferences",
                    dataRef: "3",
                },
            ],
        },
        promo: {
            path: "/promo",
            element: <Promo />,
        },
        manageRegions: {
            path: "/settings/manage-regions",
            element: <Region />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                allowedCEMRoles: featureFlag.settings.viewExemptionRegion.ecmRole,
                permissions: [Permissions.settings.region.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/advanced-settings",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Where you collect certificates",
                    dataRef: "3",
                },
            ],
        },

        customFields: {
            path: "/settings/custom-fields",
            element: <CustomFields />,
            accessConfig: {
                allowedSubscriptions: [Subscription.ECMPremium, Subscription.VEPREMIUM],
                permissions: [Permissions.settings.customerCustomFields.read],
            },
            children: {
                incomingCertificates: {
                    path: "certificates-custom-fields/incoming",
                    element: (
                        <OutgoingCertificateContext.Provider value={false}>
                            <CertificatesCustomFields />
                        </OutgoingCertificateContext.Provider>
                    ),
                    accessConfig: {
                        allowedSubscriptions: [Subscription.ECMPremium],
                    },
                },
                outgoingCertificates: {
                    path: "certificates-custom-fields/outgoing",
                    element: (
                        <OutgoingCertificateContext.Provider value>
                            <CertificatesCustomFields />
                        </OutgoingCertificateContext.Provider>
                    ),
                    accessConfig: {
                        allowedSubscriptions: [Subscription.VEPREMIUM],
                    },
                },
                customers: {
                    path: "customers-custom-fields",
                    element: <CustomersCustomFields />,
                    accessConfig: {
                        allowedSubscriptions: [Subscription.ECMPremium, Subscription.VEPREMIUM],
                    },
                },
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/advanced-settings",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Custom fields",
                    dataRef: "3",
                },
            ],
        },
        settingsRetail: {
            path: "/settings/retail",
            element: <Retail />,
            accessConfig: {
                allowedSubscriptions: featureFlag.settings.retailLocations.subscription,
                allowedCEMRoles: featureFlag.settings.viewRetailLocations.ecmRole,
                permissions: [Permissions.retailSettings.index.read],
            },
            children: {
                locations: {
                    path: "locations",
                    element: <RetailLocations />,
                    accessConfig: {
                        allowedSubscriptions: featureFlag.settings.retailLocations.subscription,
                    },
                },
                retailSettings: {
                    path: "retail-settings",
                    element: <RetailSettings />,
                    accessConfig: {
                        allowedSubscriptions: featureFlag.settings.retailLocations.subscription,
                    },
                },
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/advanced-settings",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Retail site locations and settings",
                    dataRef: "3",
                },
            ],
        },
        settingsCertificateAttachmentLabels: {
            path: "/settings/certificate-attachment-labels",
            element: <CertificateAttachmentLabels />,
            accessConfig: {
                allowedSubscriptions: featureFlag.certificateAttachmentLabels.manage.subscription,
                allowedCEMRoles: featureFlag.certificateAttachmentLabels.manage.ecmRole,
                permissions: [Permissions.settings.certificateLabel.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/advanced-settings",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Certificate Attachment Labels",
                    dataRef: "3",
                },
            ],
        },
        settingsLabels: {
            path: "/settings/labels",
            element: <Labels />,
            accessConfig: {
                allowedSubscriptions: featureFlag.settings.viewLabels.subscription,
                allowedCEMRoles: featureFlag.settings.viewLabels.ecmRole,
                permissions: [Permissions.settings.certificateLabel.read],
            },
            children: {
                certificateLabels: {
                    path: "certificate-labels",
                    element: <CertificateLabels />,
                    accessConfig: {
                        allowedSubscriptions: featureFlag.settings.viewLabels.subscription,
                    },
                },
                customerLabels: {
                    path: "customer-labels",
                    element: <CustomerLabels />,
                    accessConfig: {
                        allowedSubscriptions: featureFlag.settings.viewLabels.subscription,
                    },
                },
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/advanced-settings",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Labels",
                    dataRef: "3",
                },
            ],
        },
        settingsCompanyGroups: {
            path: "/settings/companyGroups",
            element: <CompanyGroupsSettings />,
            accessConfig: {
                allowedSubscriptions: featureFlag.settings.companyGroups.subscription,
                allowedCEMRoles: featureFlag.settings.companyGroups.ecmRole,
                permissions: [Permissions.settings.companyGroups.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/advanced-settings",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Company groups",
                    dataRef: "3",
                },
            ],
        },
        settingsBuckets: {
            path: "/settings/buckets",
            element: <BucketsSetting />,
            accessConfig: {
                allowedSubscriptions: [Subscription.ECMPremium],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/advanced-settings",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Buckets",
                    dataRef: "3",
                },
            ],
        },
        bucketLocation: {
            path: "/settings/buckets/location/:bucketId",
            element: <BucketLocationGrid />,
            accessConfig: {
                allowedSubscriptions: [Subscription.ECMPremium],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/advanced-settings",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Buckets",
                    url: "/settings/buckets",
                    dataRef: "3",
                    emitNavEvent: true,
                },
                {
                    label: "Manager locations",
                    dataRef: "4",
                },
            ],
        },
        bucketUser: {
            path: "/settings/buckets/user/:bucketId",
            element: <BucketUserGrid />,
            accessConfig: {
                allowedSubscriptions: [Subscription.ECMPremium],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/advanced-settings",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Buckets",
                    url: "/settings/buckets",
                    dataRef: "3",
                    emitNavEvent: true,
                },
                {
                    label: "Manager users",
                    dataRef: "4",
                },
            ],
        },
        customerSearch: {
            path: "/search/customers",
            element: <Customers />,
            accessConfig: {
                allowedSubscriptions: [
                    ...lowerSubscription,
                    ...advanceSubscription,
                    ...vendorAvaTaxAPSubscription,
                ],
                allowedRETRoles: [EcmRole.RETManager],
                permissions: [Permissions.customer.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Customers",
                    dataRef: "2",
                },
            ],
        },
        vendorSearch: {
            path: "/search/vendors",
            element: <Vendors />,
            accessConfig: {
                allowedSubscriptions: [...vendorSubscription, ...vendorAvaTaxAPSubscription],
                permissions: [Permissions.vendor.index.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Vendors",
                    dataRef: "2",
                },
            ],
        },
        jobsSearch: {
            path: "/search/jobs",
            element: <JobsSearch />,
            accessConfig: {
                allowedSubscriptions: advanceSubscription,
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Jobs",
                    dataRef: "2",
                },
            ],
        },
        customerDetails: {
            path: "/customer/:id",
            element: <CustomerHeader />,
            accessConfig: {
                allowedSubscriptions: [...lowerSubscription, ...ecmSubscription],
                allowedRETRoles: [EcmRole.RETManager],
                permissions: [Permissions.customer.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Customers",
                    url: "/search/customers",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Customer",
                    dataRef: "3",
                },
            ],
        },
        vendorDetails: {
            path: "/vendor/:id",
            element: <CustomerHeader />,
            accessConfig: {
                allowedSubscriptions: [...vendorSubscription, ...vendorAvaTaxAPSubscription],
                permissions: [Permissions.vendor.index.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Vendors",
                    url: "/search/vendors",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Vendor",
                    dataRef: "3",
                },
            ],
        },
        certificateSearch: {
            path: "/search/certificates",
            element: <Certificates />,
            accessConfig: {
                allowedSubscriptions: [
                    ...lowerSubscription,
                    ...ecmSubscription,
                    ...vendorAvaTaxAPSubscription,
                ],
                permissions: [Permissions.certificate.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Customer certificates",
                    dataRef: "2",
                },
            ],
        },
        pendingCertificateSearch: {
            path: "/search/pending",
            element: <PendingSearch />,
            accessConfig: {
                allowedSubscriptions: advanceSubscription,
                allowedRETRoles: [EcmRole.RETManager],
                permissions: [Permissions.pendingCertificate.view],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Incoming certificates to review",
                    dataRef: "2",
                },
            ],
        },
        addCustomer: {
            path: "/add-customer",
            element: <CustomerComponent setVendor={false} />,
            accessConfig: {
                allowedSubscriptions: [...lowerSubscription, ...ecmSubscription],
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
                permissions: [Permissions.customer.write],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Customers",
                    url: "/search/customers",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Add a customer",
                    dataRef: "3",
                },
            ],
        },
        addVendor: {
            path: "/add-vendor",
            element: <CustomerComponent setVendor={true} />,
            accessConfig: {
                allowedSubscriptions: [...vendorSubscription, ...vendorAvaTaxAPSubscription],
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
                permissions: [Permissions.vendor.index.create],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Vendors",
                    url: "/search/vendors",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Add a vendor",
                    dataRef: "2",
                },
            ],
        },
        addJob: {
            path: "/add-job",
            element: <JobForm />,
            accessConfig: {
                allowedSubscriptions: [...lowerSubscription, ...ecmSubscription],
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
                permissions: [Permissions.customer.write],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Jobs",
                    url: "/search/jobs",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Add job",
                    dataRef: "3",
                },
            ],
        },
        jobDetails: {
            path: "/jobs/:jobId",
            element: <JobDetails />,
            accessConfig: {
                allowedSubscriptions: [...lowerSubscription, ...ecmSubscription],
                allowedCEMRoles: [
                    EcmRole.CEMAdmin,
                    EcmRole.CEMManager,
                    EcmRole.CEMPreValidator,
                    EcmRole.CEMCertificateReviewer,
                ],
                permissions: [Permissions.customer.write, Permissions.customer.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Jobs",
                    url: "/search/jobs",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Job",
                    dataRef: "3",
                },
            ],
        },
        // NOTE: review, reviewCertificate, and reviewCertificateHistory are declared as
        // independent sibling routes rather than nested children. Validation.jsx (the
        // element for all three) renders its own sub-views directly based on the URL
        // params it reads, without a React Router <Outlet/>, so a child route's
        // accessConfig would never actually be enforced - only the top-level matched
        // route's <Protected> guard runs. Keeping them flat lets bare /review carry a
        // tighter accessConfig than /review/:certificateID and /review/history/:certificateID.
        review: {
            path: "/review",
            element: <Validation />,
            accessConfig: {
                allowedSubscriptions: [...lowerSubscription, ...ecmSubscription],
                allowedRETRoles: [EcmRole.RETManager],
                permissions: [Permissions.pendingCertificate.view],
            },
            breadCrumbs: REVIEW_BREADCRUMBS,
        },
        reviewCertificate: {
            path: "/review/:certificateID",
            element: <Validation />,
            accessConfig: {
                allowedSubscriptions: [...lowerSubscription, ...ecmSubscription],
                allowedRETRoles: [EcmRole.RETManager],
                permissions: [Permissions.pendingCertificate.view],
            },
            breadCrumbs: REVIEW_BREADCRUMBS,
        },
        reviewCertificateHistory: {
            path: "/review/history/:certificateID",
            element: <Validation />,
            accessConfig: {
                allowedSubscriptions: [...lowerSubscription, ...ecmSubscription],
                allowedRETRoles: [EcmRole.RETManager],
                permissions: [Permissions.pendingCertificate.view],
            },
            breadCrumbs: REVIEW_BREADCRUMBS,
        },
        aviatorReviewCertificate: {
            path: "/aviator/review/:certificateID",
            element: <AviatorValidation />,
            accessConfig: {
                allowedSubscriptions: [...lowerSubscription, ...ecmSubscription],
                allowedRETRoles: [EcmRole.RETManager],
                permissions: [Permissions.pendingCertificate.view],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Incoming certificates to review",
                    url: "/search/pending",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Aviator review exemption certificate",
                    dataRef: "3",
                },
            ],
        },
        aviatorReviewCertificateHistory: {
            path: "/aviator/review/history/:certificateID",
            element: <AviatorValidation />,
            accessConfig: {
                allowedSubscriptions: [...lowerSubscription, ...ecmSubscription],
                allowedRETRoles: [EcmRole.RETManager],
                permissions: [Permissions.pendingCertificate.view],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Incoming certificates to review",
                    url: "/search/pending",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Aviator review exemption certificate",
                    dataRef: "3",
                },
            ],
        },
        aviatorCertificate: {
            path: "/aviator/certificate/:certificateID",
            element: <AviatorCertificate />,
            accessConfig: {
                allowedSubscriptions: [
                    ...lowerSubscription,
                    ...ecmSubscription,
                    ...vendorSubscription,
                ],
                permissions: [Permissions.certificate.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Customer certificates",
                    url: "/search/certificates",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Aviator exemption certificate",
                    dataRef: "3",
                },
            ],
        },
        aviatorCertificateHistory: {
            path: "/aviator/certificate/history/:certificateID",
            element: <AviatorCertificate />,
            accessConfig: {
                allowedSubscriptions: [
                    ...lowerSubscription,
                    ...ecmSubscription,
                    ...vendorSubscription,
                ],
                permissions: [Permissions.certificate.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Customer certificates",
                    url: "/search/certificates",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Aviator exemption certificate",
                    dataRef: "3",
                },
            ],
        },
        certificate: {
            path: "/certificate",
            element: <Certificate />,
            accessConfig: {
                allowedSubscriptions: [
                    ...lowerSubscription,
                    ...ecmSubscription,
                    ...vendorSubscription,
                ],
                permissions: [Permissions.certificate.read],
            },
            children: {
                history: {
                    path: "history/:certificateID",
                    element: <ValidationHistory />,
                },
                details: {
                    path: ":certificateID?",
                    element: <CertificateDetail />,
                },
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Customer certificates",
                    url: "/search/certificates",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Review exemption certificate",
                    dataRef: "3",
                },
            ],
        },
        importCustomers: {
            path: "/import-customers",
            element: <ImportCustomers />,
            accessConfig: {
                allowedSubscriptions: [Subscription.ECMPremium, Subscription.ECMPro],
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
                permissions: [Permissions.customer.bulk],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Customers",
                    url: "/search/customers",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Import customers",
                    dataRef: "3",
                },
            ],
        },
        importCertificates: {
            path: "/import-certificates",
            element: <ImportCertificates />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...lowerSubscription],
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager],
                permissions: [Permissions.certificate.bulkUpload.create],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Customer certificates",
                    url: "/search/certificates",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Import certificates",
                    dataRef: "3",
                },
            ],
        },
        importVendors: {
            path: "/import-vendors",
            element: <ImportVendors />,
            accessConfig: {
                allowedSubscriptions: [...vendorSubscription, ...vendorAvaTaxAPSubscription],
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager],
                permissions: [Permissions.customer.import.create, Permissions.customer.import.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Vendors",
                    url: "/search/vendors",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Import vendors",
                    dataRef: "3",
                },
            ],
        },
        createRequests: {
            path: "/requests/create",
            element: <Requests />,
            accessConfig: {
                allowedSubscriptions: advanceSubscription,
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
                permissions: [Permissions.certificateRequests.index.create],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Customers",
                    url: "/search/customers",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Create request",
                    dataRef: "3",
                },
            ],
        },
        requestsDashboard: {
            path: "/requests",
            element: <RequestDashboard />,
            accessConfig: {
                allowedSubscriptions: advanceSubscription,
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
                permissions: [Permissions.certificateRequests.index.read],
            },
            children: {
                index: {
                    index: true,
                    element: <BulkRequest />,
                    accessConfig: {
                        allowedSubscriptions: advanceSubscription,
                    },
                },
                singleRequests: {
                    path: "singleRequests",
                    element: <SingleRequest />,
                    accessConfig: {
                        allowedSubscriptions: advanceSubscription,
                    },
                },
                bulkRequests: {
                    path: "bulkRequests",
                    element: <BulkRequest />,
                    accessConfig: {
                        allowedSubscriptions: advanceSubscription,
                    },
                },
                deletedRequests: {
                    path: "deletedRequests",
                    element: <BulkRequest />,
                    accessConfig: {
                        allowedSubscriptions: advanceSubscription,
                    },
                },
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Certificate requests",
                    dataRef: "2",
                },
            ],
        },
        campaign: {
            path: "/campaigns/:campaignID",
            element: <Campaign />,
            accessConfig: {
                allowedSubscriptions: advanceSubscription,
                permissions: [Permissions.certificateRequests.index.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Certificate requests",
                    url: "/requests/bulkRequests",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Request",
                    dataRef: "3",
                },
            ],
        },
        taxAnswers: {
            path: "/resources/tax-answers",
            element: <TaxAnswers />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Resources",
                    url: "/resources",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Tax answers",
                    dataRef: "3",
                },
            ],
        },
        taxLaws: {
            path: "/resources/tax-laws",
            element: <TaxLaws />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Resources",
                    url: "/resources",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Tax laws",
                    dataRef: "3",
                },
            ],
        },
        exemptEntities: {
            path: "/resources/exempt-entities",
            element: <ExemptEntities />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Resources",
                    url: "/resources",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Exempt entities",
                    dataRef: "3",
                },
            ],
        },
        certificateForms: {
            path: "/resources/certificate-forms",
            element: <CertificateForms />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Resources",
                    url: "/resources",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Exemption certificate forms",
                    dataRef: "3",
                },
            ],
        },
        resources: {
            path: "/resources",
            element: <Resources />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Resources",
                    dataRef: "2",
                },
            ],
        },
        customersSavedSearch: {
            path: "/saved/search/customers",
            element: <Customers />,
            accessConfig: {
                allowedSubscriptions: [...lowerSubscription, ...ecmSubscription],
                permissions: [Permissions.savedSearch.index.read],
            },
        },
        certificatesSavedSearch: {
            path: "/saved/search/certificates",
            element: <Certificates />,
            accessConfig: {
                allowedSubscriptions: [...lowerSubscription, ...ecmSubscription],
                permissions: [Permissions.savedSearch.index.read],
            },
        },
        pendingCertificatesSavedSearch: {
            path: "/saved/search/pending-certificate",
            element: <PendingSearch />,
            accessConfig: {
                allowedSubscriptions: advanceSubscription,
                permissions: [Permissions.savedSearch.index.read],
            },
        },
        vendorCertificatesSavedSearch: {
            path: "/saved/search/vendor-certificates",
            element: <VendorCertificates />,
            accessConfig: {
                allowedSubscriptions: vendorSubscription,
            },
        },
        vendorsSavedSearch: {
            path: "/saved/search/vendors",
            element: <Vendors />,
            accessConfig: {
                allowedSubscriptions: vendorSubscription,
            },
        },
        certificateRequestFormsContent: {
            path: "/certificate-forms",
            element: <CertificateRequestFormsContent />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
            },
        },
        vendorRequest: {
            path: "/vendor-requests/:id/",
            element: <VendorRequest />,
            accessConfig: {
                allowedSubscriptions: vendorSubscription,
                permissions: [Permissions.vendor.certificate.create],
            },
        },
        vendorCertificatesSearch: {
            path: "/search/vendor-certificates",
            element: <VendorCertificateHeader />,
            accessConfig: {
                allowedSubscriptions: vendorSubscription,
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Vendor certificates",
                    dataRef: "2",
                },
            ],
        },
        vendorPackagesSearch: {
            path: "/search/vendor-packages",
            element: <VendorCertificateHeader />,
            accessConfig: {
                allowedSubscriptions: vendorPremiumOnlySubscription,
                permissions: [Permissions.vendor.packages.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Vendor certificates",
                    dataRef: "2",
                },
            ],
        },
        createVendorPackages: {
            path: "/search/vendor-packages/create",
            element: <VendorPackageCreate />,
            accessConfig: {
                allowedSubscriptions: vendorPremiumOnlySubscription,
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
                permissions: [Permissions.vendor.packages.create],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Vendor certificates",
                    url: "/search/vendor-packages",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Create a certificate package",
                    dataRef: "3",
                },
            ],
        },
        editVendorPackages: {
            path: "/search/vendor-packages/add-certificates/:id",
            element: <VendorPackageEdit />,
            accessConfig: {
                allowedSubscriptions: vendorPremiumOnlySubscription,
                permissions: [Permissions.vendor.packages.update],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Vendor certificates",
                    url: "/search/vendor-packages",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Add certificate to package",
                    dataRef: "3",
                },
            ],
        },
        vendorPackageDetails: {
            path: "/search/vendor-packages/:id",
            element: <VendorPackageContent />,
            accessConfig: {
                allowedSubscriptions: vendorPremiumOnlySubscription,
                permissions: [Permissions.vendor.packages.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Vendor certificates",
                    url: "/search/vendor-packages",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Package details",
                    dataRef: "3",
                },
            ],
        },
        sendPackage: {
            path: "/send-package/:id",
            element: <SendPackage />,
            accessConfig: {
                allowedSubscriptions: vendorPremiumOnlySubscription,
                permissions: [Permissions.vendor.packages.create],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Vendor certificates",
                    url: "/search/vendor-packages",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Send package",
                    dataRef: "3",
                },
            ],
        },
        addCoverLetter: {
            path: "/add-cover-letter",
            element: <CoverLetterComponent />,
            accessConfig: {
                allowedSubscriptions: advanceSubscription,
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
                permissions: [Permissions.coverLetter.index.create],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/certificateRequests",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Manage templates",
                    url: "/cover-letters/requests",
                    dataRef: "3",
                    emitNavEvent: true,
                },
                {
                    label: "Create a request template",
                    dataRef: "4",
                },
            ],
        },
        addVendorLetter: {
            path: "/add-vendor-letter",
            element: <CoverLetterComponent isVendor={true} />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorPremiumOnlySubscription],
                permissions: [Permissions.coverLetter.outgoing.create],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/certificateRequests",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Manage templates",
                    url: "/cover-letters/requests",
                    dataRef: "3",
                    emitNavEvent: true,
                },
                {
                    label: "Create a vendor template",
                    dataRef: "4",
                },
            ],
        },
        coverLetterDetails: {
            path: "/cover-letter/:id",
            element: <CoverLetterComponent isVendor={false} />,
            accessConfig: {
                allowedSubscriptions: advanceSubscription,
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
                permissions: [Permissions.coverLetter.index.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/certificateRequests",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Manage templates",
                    url: "/cover-letters/requests",
                    dataRef: "3",
                    emitNavEvent: true,
                },
                {
                    label: "Edit request template",
                    dataRef: "4",
                },
            ],
        },
        vendorLetterDetails: {
            path: "/vendor-letter/:id",
            element: <CoverLetterComponent isVendor={true} />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                permissions: [Permissions.coverLetter.outgoing.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/certificateRequests",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Manage templates",
                    url: "/cover-letters/requests",
                    dataRef: "3",
                    emitNavEvent: true,
                },
                {
                    label: "Edit vendor template",
                    dataRef: "4",
                },
            ],
        },
        bulkUploadCertificates: {
            path: "/search/bulk-upload",
            element: <BulkUploadCertificates />,
            accessConfig: {
                allowedSubscriptions: advanceSubscription,
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager],
                permissions: [Permissions.pendingCertificate.write],
            },
        },
        // TODO: This route and its unused components will be removed after non IAM deprecation
        roleManagement: {
            path: "/user-role-management/:userId",
            element: <RoleManagementPage />,
            accessConfig: {
                allowedSubscriptions: userRoleAdminSubscription,
                allowedCEMRoles: [EcmRole.CEMAdmin],
            },
        },
        manageExemptReasons: {
            path: "/settings/manage-exempt-reasons",
            element: <ManageExemptionReasons />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                allowedCEMRoles: featureFlag.settings.viewExemptionReason.ecmRole,
                permissions: [Permissions.exemptionReasons.index.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/advanced-settings",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Exempt reasons",
                    dataRef: "3",
                },
            ],
        },
        healthCheck: {
            path: "/health-check",
            element: <HealthCheck />,
        },
        manageInvalidReasons: {
            path: "/settings/manage-invalid-reasons",
            element: <ManageInvalidReasons />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                allowedCEMRoles: featureFlag.settings.viewInvalidReason.ecmRole,
                permissions: [Permissions.invalidReasons.index.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/advanced-settings",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Invalid reasons",
                    dataRef: "3",
                },
            ],
        },
        manageProductCategories: {
            path: "/settings/manage-product-categories",
            element: <ManageProductCategories />,
            accessConfig: {
                allowedSubscriptions: advanceSubscription,
                allowedCEMRoles: featureFlag.settings.viewProductCategories.ecmRole,
                permissions: [Permissions.settings.exemptProductCategories.read],
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/advanced-settings",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Exempt product categories",
                    dataRef: "3",
                },
            ],
        },
        reports: {
            path: "/reports",
            element: <ECMReports />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                permissions: [Permissions.savedSearch.index.read],
            },
            children: {
                index: {
                    index: true,
                    element: <SavedSearches />,
                    accessConfig: {
                        allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                    },
                },
                savedSearches: {
                    path: "saved-searches",
                    element: <SavedSearches />,
                    accessConfig: {
                        allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                    },
                },
                adminReports: {
                    path: "admin-reports",
                    element: <AdminReports />,
                    accessConfig: {
                        allowedSubscriptions: advanceSubscription,
                        allowedCEMRoles: [EcmRole.CEMAdmin],
                        permissions: [Permissions.adminReports.index.read],
                    },
                },
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Customer search",
                    url: "/search/customers",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "ECM reports",
                    dataRef: "3",
                },
            ],
        },
        iamEnablement: {
            path: "/settings/iam-enablement",
            element: <IamEnablement />,
        },
        iamEnablementDetails: {
            path: "/settings/iam-enablement/:tenantId",
            element: <IamEnablementDetails />,
        },
        iamEnablementLogs: {
            path: "/settings/iam-enablement/logs/:avaTaxAccountId",
            element: <IamEnablementLogs />,
        },
        iamEnablementLogsLegacy: {
            // Back-compat: support old URLs that include jobExternalId
            path: "/settings/iam-enablement/logs/:avaTaxAccountId/:jobExternalId",
            element: <IamEnablementLogs />,
        },
        bulkEnablementLogs: {
            path: "/settings/iam-enablement/bulk-jobs",
            element: <BulkEnablementJobs />,
        },
        coverLetters: {
            path: "/cover-letters",
            element: <CoverLetterHeader />,
            accessConfig: {
                allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                allowedCEMRoles: [EcmRole.CEMAdmin, EcmRole.CEMManager, EcmRole.CEMPreValidator],
                permissions: [
                    Permissions.coverLetter.index.read,
                    Permissions.coverLetter.outgoing.read,
                ],
                requireAnyPermission: true,
            },
            children: {
                index: {
                    index: true,
                    element: <CoverLetterGrid />,
                    accessConfig: {
                        allowedSubscriptions: advanceSubscription,
                        permissions: [Permissions.coverLetter.index.read],
                    },
                },
                requests: {
                    path: "requests",
                    element: <CoverLetterGrid />,
                    accessConfig: {
                        allowedSubscriptions: advanceSubscription,
                        permissions: [Permissions.coverLetter.index.read],
                    },
                },
                vendors: {
                    path: "vendors",
                    element: <VendorLetterGrid />,
                    accessConfig: {
                        allowedSubscriptions: [...advanceSubscription, ...vendorSubscription],
                        permissions: [Permissions.coverLetter.outgoing.read],
                    },
                },
            },
            breadCrumbs: [
                EXEMPTIONS_BREADCRUMB,
                {
                    label: "Exemption certificates",
                    url: "/settings/certificateRequests",
                    dataRef: "2",
                    emitNavEvent: true,
                },
                {
                    label: "Manage templates",
                    dataRef: "3",
                },
            ],
        },
        ecms: {
            path: "/ecms",
            element: <ECMS />,
            accessConfig: {
                internalUser: true,
            },
        },
    })
);

/**
 * Freezes the commonRoutesConfig object to prevent accidental modifications at runtime.
 * Then builds and exports the full path-based route structure using a helper utility.
 */
export default commonRoutesConfig;
